/**
 * Settings Page
 *
 * Application settings and configuration.
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Settings, User, Pencil, Building2, Mail, Phone, MapPin, Save, X, Camera, Plus, Trash2 } from 'lucide-react'

import { useAuth } from '@/hooks/useAuth'
import { useTranslation } from '@/hooks/useTranslation'
import { formatCurrency } from '@/lib/utils'
import { useTenant, useUpdateTenant } from '@/hooks/useTenant'
import { useGeography } from '@/hooks/useGeography'
import { AlanubePanel } from '@/components/settings'
import { UserAvatar, AvatarSelector } from '@/components/AvatarSelector'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/components/ui/toaster'
import { Skeleton } from '@/components/ui/skeleton'
import api from '@/lib/api'

interface Parameter {
  id: number
  code: string
  value: number | null
  description: string
}

// Claves de i18n de cada parámetro (se traducen al mostrarlas)
const PARAMETER_LABELS: Record<string, { label: string; description: string }> = {
  dolar_value: { label: 'settings.params.exchangeRate', description: 'settings.params.exchangeRateDesc' },
  tax_rate: { label: 'settings.params.taxRate', description: 'settings.params.taxRateDesc' },
  default_profit_1: { label: 'settings.params.marginRetail', description: 'settings.params.level1' },
  default_profit_2: { label: 'settings.params.marginWholesale', description: 'settings.params.level2' },
  default_profit_3: { label: 'settings.params.marginDistributor', description: 'settings.params.level3' },
}

export function SettingsPage() {
  const navigate = useNavigate()
  const { user, isAdmin, refreshUser } = useAuth()
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  // Dialog states
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [avatarSelectorOpen, setAvatarSelectorOpen] = useState(false)

  // Parameter edit dialog
  const [paramDialogOpen, setParamDialogOpen] = useState(false)
  const [editingParam, setEditingParam] = useState<Parameter | null>(null)
  const [paramValue, setParamValue] = useState('')

  // Avatar update mutation
  const updateAvatarMutation = useMutation({
    mutationFn: async (avatarId: string) => {
      const response = await api.put('/users/me', { avatar: avatarId })
      return response.data
    },
    onSuccess: () => {
      refreshUser()
      queryClient.invalidateQueries({ queryKey: ['current-user'] })
      setAvatarSelectorOpen(false)
      toast({
        title: t('common.success'),
        description: t('users.avatarUpdated'),
      })
    },
    onError: () => {
      toast({
        title: t('common.error'),
        description: t('common.errorOccurred'),
        variant: 'destructive',
      })
    },
  })

  // Tenant (company) info
  const { tenant, isLoading: loadingTenant, refreshTenant } = useTenant()
  const updateTenantMutation = useUpdateTenant()
  const [isEditingCompany, setIsEditingCompany] = useState(false)
  const [companyForm, setCompanyForm] = useState({
    name: '',
    trade_name: '',
    identification_type: '02',
    identification: '',
    email: '',
    phone: '',
    phone_country_code: '506',
    address: '',
    province_id: null as number | null,
    canton_id: null as number | null,
    district_id: null as number | null,
    headquarters: '001',
    terminal: '00001',
    activity_codes: [] as Array<{ code: string; name: string; tax_rate: string }>,
  })

  // Geography data for location selectors
  const { provinces, cantons, districts } = useGeography(
    companyForm.province_id,
    companyForm.canton_id
  )

  // Initialize company form when data loads
  useEffect(() => {
    if (tenant) {
      setCompanyForm({
        name: tenant.name || '',
        trade_name: tenant.trade_name || '',
        identification_type: tenant.identification_type || '02',
        identification: tenant.identification || '',
        email: tenant.email || '',
        phone: tenant.phone || '',
        phone_country_code: tenant.phone_country_code || '506',
        address: tenant.address || '',
        province_id: tenant.province_id,
        canton_id: tenant.canton_id,
        district_id: tenant.district_id,
        headquarters: tenant.headquarters || '001',
        terminal: tenant.terminal || '00001',
        activity_codes: (tenant.activity_codes || []).map(a =>
          typeof a === 'string'
            ? { code: a, name: a, tax_rate: '01' }
            : { code: a.code || '', name: a.name || '', tax_rate: a.tax_rate || '01' }
        ),
      })
    }
  }, [tenant])

  // Fetch parameters
  const { data: parameters, isLoading: loadingParams } = useQuery({
    queryKey: ['parameters'],
    queryFn: async () => {
      const response = await api.get('/parameters')
      return response.data as Parameter[]
    },
    enabled: isAdmin,
  })

  // Update parameter mutation
  const updateParamMutation = useMutation({
    mutationFn: async ({ code, value }: { code: string; value: number }) => {
      const response = await api.put(`/parameters/${code}`, { value })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parameters'] })
      toast({
        title: t('common.success'),
        description: t('settings.params.updated'),
      })
      setParamDialogOpen(false)
      setEditingParam(null)
    },
    onError: () => {
      toast({
        title: t('common.error'),
        description: t('settings.params.updateFailed'),
        variant: 'destructive',
      })
    },
  })

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast({
        title: t('common.error'),
        description: t('settings.passwordsDoNotMatch'),
        variant: 'destructive',
      })
      return
    }

    if (newPassword.length < 6) {
      toast({
        title: t('common.error'),
        description: t('settings.passwordTooShort'),
        variant: 'destructive',
      })
      return
    }

    setIsChangingPassword(true)
    try {
      await api.post('/users/me/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      })
      toast({
        title: t('common.success'),
        description: t('settings.passwordChanged'),
      })
      setPasswordDialogOpen(false)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : t('settings.passwordChangeError')
      toast({
        title: t('common.error'),
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsChangingPassword(false)
    }
  }

  const handleEditParam = (param: Parameter) => {
    setEditingParam(param)
    setParamValue(param.value?.toString() || '')
    setParamDialogOpen(true)
  }

  const handleSaveParam = () => {
    if (!editingParam) return
    const value = parseFloat(paramValue)
    if (isNaN(value)) {
      toast({
        title: t('common.error'),
        description: t('settings.params.invalidNumber'),
        variant: 'destructive',
      })
      return
    }
    updateParamMutation.mutate({ code: editingParam.code, value })
  }

  const handleSaveCompanyInfo = () => {
    // Validate activity codes format: XXXX.X (Hacienda)
    const activityCodeRegex = /^\d{4}\.\d$/
    for (const activity of companyForm.activity_codes) {
      if (!activity.code || !activityCodeRegex.test(activity.code)) {
        toast({
          title: t('common.error'),
          description: t('settings.company.invalidActivityCode', { code: activity.code || '' }),
          variant: 'destructive',
        })
        return
      }
      if (!activity.name.trim()) {
        toast({
          title: t('common.error'),
          description: t('settings.company.activityNameRequired'),
          variant: 'destructive',
        })
        return
      }
    }
    updateTenantMutation.mutate(companyForm, {
      onSuccess: () => {
        toast({
          title: t('common.success'),
          description: t('settings.company.updated'),
        })
        setIsEditingCompany(false)
        refreshTenant()
      },
      onError: () => {
        toast({
          title: t('common.error'),
          description: t('settings.company.updateFailed'),
          variant: 'destructive',
        })
      },
    })
  }

  const handleCancelCompanyEdit = () => {
    // Reset form to current values
    if (tenant) {
      setCompanyForm({
        name: tenant.name || '',
        trade_name: tenant.trade_name || '',
        identification_type: tenant.identification_type || '02',
        identification: tenant.identification || '',
        email: tenant.email || '',
        phone: tenant.phone || '',
        phone_country_code: tenant.phone_country_code || '506',
        address: tenant.address || '',
        province_id: tenant.province_id,
        canton_id: tenant.canton_id,
        district_id: tenant.district_id,
        headquarters: tenant.headquarters || '001',
        terminal: tenant.terminal || '00001',
        activity_codes: (tenant.activity_codes || []).map(a =>
          typeof a === 'string'
            ? { code: a, name: a, tax_rate: '01' }
            : { code: a.code || '', name: a.name || '', tax_rate: a.tax_rate || '01' }
        ),
      })
    }
    setIsEditingCompany(false)
  }

  // Handle province change - reset canton and district
  const handleProvinceChange = (value: string) => {
    const provinceId = parseInt(value)
    setCompanyForm(prev => ({
      ...prev,
      province_id: provinceId,
      canton_id: null,
      district_id: null,
    }))
  }

  // Handle canton change - reset district
  const handleCantonChange = (value: string) => {
    const cantonId = parseInt(value)
    setCompanyForm(prev => ({
      ...prev,
      canton_id: cantonId,
      district_id: null,
    }))
  }

  // Handle district change
  const handleDistrictChange = (value: string) => {
    const districtId = parseInt(value)
    setCompanyForm(prev => ({
      ...prev,
      district_id: districtId,
    }))
  }

  // Toggle invoicing mutation
  const toggleInvoicingMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/tenants/toggle-invoicing')
      return response.data
    },
    onSuccess: (data) => {
      refreshTenant()
      toast({
        title: data.enabled ? t('settings.invoicingEnabledToast') : t('settings.invoicingDisabledToast'),
      })
    },
    onError: (error: any) => {
      toast({
        title: t('common.error'),
        description: error.response?.data?.detail || t('settings.toggleFailed'),
        variant: 'destructive',
      })
    },
  })

  // Get parameter value by code
  const getParamValue = (code: string): string => {
    const param = parameters?.find(p => p.code === code)
    if (!param || param.value === null) return '-'
    if (code === 'dolar_value') return formatCurrency(param.value)
    if (code === 'tax_rate' || code.startsWith('default_profit')) return `${param.value}%`
    return param.value.toString()
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t('settings.title')}</h1>
        <p className="text-muted-foreground">
          {t('settings.subtitle')}
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* User Profile */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              {t('settings.myProfile')}
            </CardTitle>
            <CardDescription>
              {t('settings.accountInfo')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="relative">
                <UserAvatar
                  avatarId={user?.avatar}
                  name={user?.full_name || 'Usuario'}
                  size="xl"
                />
                <Button
                  size="icon"
                  variant="secondary"
                  className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full shadow-md"
                  onClick={() => setAvatarSelectorOpen(true)}
                >
                  <Camera className="h-4 w-4" />
                </Button>
              </div>
              <div>
                <h3 className="font-medium text-lg">{user?.full_name}</h3>
                <p className="text-sm text-muted-foreground capitalize">
                  {user?.role}
                </p>
              </div>
            </div>
            <div className="space-y-2 pt-4 border-t">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('settings.username')}</span>
                <span>{user?.username}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('settings.email')}</span>
                <span>{user?.email || t('settings.notConfigured')}</span>
              </div>
            </div>
            <Button variant="outline" className="w-full" onClick={() => setPasswordDialogOpen(true)}>
              {t('settings.changePassword')}
            </Button>
          </CardContent>
        </Card>

        {/* System Parameters (Admin only) */}
        {isAdmin && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                {t('settings.systemParams')}
              </CardTitle>
              <CardDescription>
                {t('settings.generalConfig')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingParams ? (
                <div className="space-y-3">
                  <Skeleton className="h-12" />
                  <Skeleton className="h-12" />
                  <Skeleton className="h-12" />
                </div>
              ) : (
                <>
                  {/* Exchange Rate */}
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">{t('settings.exchangeRate')}</p>
                      <p className="text-sm text-muted-foreground">{getParamValue('dolar_value')}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const param = parameters?.find(p => p.code === 'dolar_value')
                        if (param) handleEditParam(param)
                      }}
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      {t('settings.edit')}
                    </Button>
                  </div>

                  {/* Tax Rate */}
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">{t('settings.tax')}</p>
                      <p className="text-sm text-muted-foreground">{getParamValue('tax_rate')}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const param = parameters?.find(p => p.code === 'tax_rate')
                        if (param) handleEditParam(param)
                      }}
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      {t('settings.edit')}
                    </Button>
                  </div>

                  {/* Profit Margins */}
                  <div className="pt-2 border-t">
                    <p className="font-medium mb-2">{t('settings.params.profitMargins')}</p>
                    <div className="space-y-2">
                      {['default_profit_1', 'default_profit_2', 'default_profit_3'].map((code) => {
                        const param = parameters?.find(p => p.code === code)
                        const labels = PARAMETER_LABELS[code]
                        return (
                          <div key={code} className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground">{labels ? t(labels.description) : code}</span>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{getParamValue(code)}</span>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => param && handleEditParam(param)}
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>

                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Company Information (Admin only) */}
        {isAdmin && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    {t('settings.companyData')}
                  </CardTitle>
                  <CardDescription>
                    {t('settings.company.description')}
                  </CardDescription>
                </div>
                {!isEditingCompany && tenant?.name && (
                  <Button variant="ghost" size="sm" onClick={() => setIsEditingCompany(true)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingTenant ? (
                <div className="space-y-3">
                  <Skeleton className="h-12" />
                  <Skeleton className="h-12" />
                </div>
              ) : isEditingCompany ? (
                // Inline editing mode
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="companyName">{t('settings.company.legalName')} *</Label>
                    <Input
                      id="companyName"
                      placeholder={t('settings.company.legalNamePlaceholder')}
                      value={companyForm.name}
                      onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tradeName">{t('settings.company.tradeName')}</Label>
                    <Input
                      id="tradeName"
                      placeholder={t('settings.company.tradeNamePlaceholder')}
                      value={companyForm.trade_name}
                      onChange={(e) => setCompanyForm({ ...companyForm, trade_name: e.target.value })}
                    />
                  </div>

                  {/* Identification */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('settings.company.idType')} *</Label>
                      <Select
                        value={companyForm.identification_type}
                        onValueChange={(value) => setCompanyForm({ ...companyForm, identification_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('settings.company.selectType')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="01">{t('idTypes.type01')}</SelectItem>
                          <SelectItem value="02">{t('idTypes.type02')}</SelectItem>
                          <SelectItem value="03">{t('idTypes.type03')}</SelectItem>
                          <SelectItem value="04">{t('idTypes.type04')}</SelectItem>
                          <SelectItem value="05">{t('idTypes.type05')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="identification">{t('settings.company.idNumber')} *</Label>
                      <Input
                        id="identification"
                        placeholder={companyForm.identification_type === '02' ? '3101234567' : '123456789'}
                        value={companyForm.identification}
                        onChange={(e) => setCompanyForm({ ...companyForm, identification: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Contact */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="companyEmail">{t('settings.email')} *</Label>
                      <Input
                        id="companyEmail"
                        type="email"
                        placeholder={t('settings.company.emailPlaceholder')}
                        value={companyForm.email}
                        onChange={(e) => setCompanyForm({ ...companyForm, email: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="companyPhone">{t('settings.company.phone')}</Label>
                      <Input
                        id="companyPhone"
                        type="tel"
                        inputMode="tel"
                        placeholder="22222222"
                        value={companyForm.phone}
                        onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Location - Required for Alanube */}
                  <div className="pt-2 border-t">
                    <p className="text-sm font-medium mb-3">{t('settings.company.locationTitle')}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="space-y-2">
                        <Label>{t('settings.company.province')} *</Label>
                        <Select
                          value={companyForm.province_id?.toString() || ''}
                          onValueChange={handleProvinceChange}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t('settings.company.province')} />
                          </SelectTrigger>
                          <SelectContent>
                            {provinces.map((p) => (
                              <SelectItem key={p.id} value={p.id.toString()}>
                                {p.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('settings.company.canton')} *</Label>
                        <Select
                          value={companyForm.canton_id?.toString() || ''}
                          onValueChange={handleCantonChange}
                          disabled={!companyForm.province_id}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t('settings.company.canton')} />
                          </SelectTrigger>
                          <SelectContent>
                            {cantons.map((c) => (
                              <SelectItem key={c.id} value={c.id.toString()}>
                                {c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('settings.company.district')} *</Label>
                        <Select
                          value={companyForm.district_id?.toString() || ''}
                          onValueChange={handleDistrictChange}
                          disabled={!companyForm.canton_id}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t('settings.company.district')} />
                          </SelectTrigger>
                          <SelectContent>
                            {districts.map((d) => (
                              <SelectItem key={d.id} value={d.id.toString()}>
                                {d.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Address detail */}
                  <div className="space-y-2">
                    <Label htmlFor="companyAddress">{t('settings.company.address')}</Label>
                    <Input
                      id="companyAddress"
                      placeholder={t('settings.company.addressPlaceholder')}
                      value={companyForm.address}
                      onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })}
                    />
                  </div>

                  {/* Hacienda config */}
                  <div className="pt-2 border-t">
                    <p className="text-sm font-medium mb-3">{t('settings.registration.haciendaConfig')}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="headquarters">{t('settings.registration.branch')}</Label>
                        <Input
                          id="headquarters"
                          inputMode="numeric"
                          placeholder="001"
                          maxLength={3}
                          value={companyForm.headquarters}
                          onChange={(e) => setCompanyForm({ ...companyForm, headquarters: e.target.value.replace(/\D/g, '') })}
                          onBlur={() => setCompanyForm({ ...companyForm, headquarters: (companyForm.headquarters || '1').padStart(3, '0') })}
                        />
                        <p className="text-xs text-muted-foreground">{t('settings.registration.branchHint')}</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="terminal">{t('settings.registration.terminal')}</Label>
                        <Input
                          id="terminal"
                          inputMode="numeric"
                          placeholder="00001"
                          maxLength={5}
                          value={companyForm.terminal}
                          onChange={(e) => setCompanyForm({ ...companyForm, terminal: e.target.value.replace(/\D/g, '') })}
                          onBlur={() => setCompanyForm({ ...companyForm, terminal: (companyForm.terminal || '1').padStart(5, '0') })}
                        />
                        <p className="text-xs text-muted-foreground">{t('settings.registration.terminalHint')}</p>
                      </div>
                    </div>
                    <div className="space-y-3 mt-4">
                      <div className="flex items-center justify-between">
                        <Label>{t('activities.economicActivities')} *</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setCompanyForm({
                            ...companyForm,
                            activity_codes: [...companyForm.activity_codes, { code: '', name: '', tax_rate: '01' }]
                          })}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          {t('activities.addActivity')}
                        </Button>
                      </div>
                      {companyForm.activity_codes.length === 0 && (
                        <p className="text-sm text-warning">{t('activities.noActivities')}</p>
                      )}
                      {companyForm.activity_codes.map((activity, idx) => (
                        <div key={idx} className="flex gap-2 items-start">
                          <div className="w-24">
                            <Input
                              placeholder="0000.0"
                              maxLength={6}
                              value={activity.code}
                              onChange={(e) => {
                                const val = e.target.value.replace(/[^\d.]/g, '')
                                const updated = [...companyForm.activity_codes]
                                updated[idx] = { ...updated[idx], code: val }
                                setCompanyForm({ ...companyForm, activity_codes: updated })
                              }}
                            />
                          </div>
                          <div className="flex-1">
                            <Input
                              placeholder={t('settings.company.activityName')}
                              value={activity.name}
                              onChange={(e) => {
                                const updated = [...companyForm.activity_codes]
                                updated[idx] = { ...updated[idx], name: e.target.value }
                                setCompanyForm({ ...companyForm, activity_codes: updated })
                              }}
                            />
                          </div>
                          <div className="w-28">
                            <Select
                              value={activity.tax_rate}
                              onValueChange={(v) => {
                                const updated = [...companyForm.activity_codes]
                                updated[idx] = { ...updated[idx], tax_rate: v }
                                setCompanyForm({ ...companyForm, activity_codes: updated })
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="01">13%</SelectItem>
                                <SelectItem value="02">4%</SelectItem>
                                <SelectItem value="03">2%</SelectItem>
                                <SelectItem value="04">1%</SelectItem>
                                <SelectItem value="05">0%</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="shrink-0"
                            disabled={companyForm.activity_codes.length <= 1}
                            onClick={() => {
                              const updated = companyForm.activity_codes.filter((_, i) => i !== idx)
                              setCompanyForm({ ...companyForm, activity_codes: updated })
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      ))}
                      <p className="text-xs text-muted-foreground">
                        Código en formato Hacienda (ej: 4530.0, 0141.1), nombre y tarifa IVA.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={handleCancelCompanyEdit}
                    >
                      <X className="h-4 w-4 mr-2" />
                      {t('common.cancel')}
                    </Button>
                    <Button
                      className="flex-1"
                      onClick={handleSaveCompanyInfo}
                      disabled={updateTenantMutation.isPending}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {updateTenantMutation.isPending ? t('common.saving') : t('common.save')}
                    </Button>
                  </div>
                </div>
              ) : tenant?.name ? (
                // Display mode
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Building2 className="h-4 w-4 text-muted-foreground mt-1" />
                    <div>
                      <p className="font-medium">{tenant.display_name}</p>
                      {tenant.trade_name && tenant.trade_name !== tenant.name && (
                        <p className="text-sm text-muted-foreground">{tenant.name}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-muted-foreground">{t('settings.company.idShort')}:</span>
                    <span className="font-medium">{tenant.identification}</span>
                    <Badge variant="outline" className="text-xs">
                      {['01', '02', '03', '04', '05'].includes(tenant.identification_type)
                        ? t(`idTypes.type${tenant.identification_type}`)
                        : t('common.other')}
                    </Badge>
                  </div>
                  {tenant.email && (
                    <div className="flex items-center gap-3 text-sm">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span>{tenant.email}</span>
                    </div>
                  )}
                  {tenant.phone && (
                    <div className="flex items-center gap-3 text-sm">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{tenant.phone_country_code} {tenant.phone}</span>
                    </div>
                  )}
                  {(tenant.province_id || tenant.address) && (
                    <div className="flex items-center gap-3 text-sm">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">
                        {provinces.find(p => p.id === tenant.province_id)?.name}
                        {tenant.canton_id && `, ${cantons.find(c => c.id === tenant.canton_id)?.name || ''}`}
                        {tenant.district_id && `, ${districts.find(d => d.id === tenant.district_id)?.name || ''}`}
                        {tenant.address && ` - ${tenant.address}`}
                      </span>
                    </div>
                  )}
                  {!tenant.province_id && (
                    <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-sm text-yellow-800 dark:text-yellow-200">
                      {t('settings.company.locationMissing')}
                    </div>
                  )}
                  <div className="pt-2 border-t text-sm space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('settings.company.branchTerminal')}:</span>
                      <span className="font-mono">{tenant.headquarters} / {tenant.terminal}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t('activities.economicActivities')}:</span>
                      {tenant.activity_codes && tenant.activity_codes.length > 0 ? (
                        <div className="mt-1 space-y-1">
                          {tenant.activity_codes.map((a: any, i: number) => {
                            // Safely extract strings from any format
                            const code = String(typeof a === 'string' ? a : (a?.code ?? ''))
                            const name = String(typeof a === 'string' ? a : (a?.name ?? ''))
                            const taxRate = String(typeof a === 'string' ? '01' : (a?.tax_rate ?? '01'))
                            return (
                              <div key={i} className="flex items-center gap-2 text-sm">
                                <span className="font-mono">{code}</span>
                                <span className="text-muted-foreground">-</span>
                                <span>{name}</span>
                                <Badge variant="outline" className="text-xs ml-auto">
                                  {taxRate === '01' ? '13%' : taxRate === '02' ? '4%' : taxRate === '03' ? '2%' : taxRate === '04' ? '1%' : '0%'}
                                </Badge>
                              </div>
                            )
                          })}
                        </div>
                      ) : (
                        <span className="text-warning ml-2">{t('settings.notConfigured')}</span>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted-foreground mb-4">{t('settings.company.empty')}</p>
                  <Button onClick={() => setIsEditingCompany(true)}>
                    {t('settings.company.configure')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Electronic Invoicing Status - Using AlanubePanel */}
        {isAdmin && (
          <AlanubePanel
            onRegister={() => navigate('/settings/hacienda-registration')}
            onToggleInvoicing={async () => {
              await toggleInvoicingMutation.mutateAsync()
            }}
            isToggling={toggleInvoicingMutation.isPending}
            onRenewCertificate={() => navigate('/settings/renew-certificate')}
          />
        )}

      </div>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('settings.changePassword')}</DialogTitle>
            <DialogDescription>
              {t('settings.changePasswordDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">{t('settings.currentPassword')}</Label>
              <Input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword">{t('settings.newPassword')}</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t('settings.confirmPassword')}</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleChangePassword} disabled={isChangingPassword}>
              {isChangingPassword ? t('common.saving') : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Parameter Edit Dialog */}
      <Dialog open={paramDialogOpen} onOpenChange={setParamDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('settings.params.editTitle', {
                name: editingParam
                  ? (PARAMETER_LABELS[editingParam.code] ? t(PARAMETER_LABELS[editingParam.code].label) : editingParam.code)
                  : t('settings.params.parameter'),
              })}
            </DialogTitle>
            <DialogDescription>
              {editingParam && PARAMETER_LABELS[editingParam.code] ? t(PARAMETER_LABELS[editingParam.code].description) : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="paramValue">{t('settings.params.value')}</Label>
              <Input
                id="paramValue"
                type="number"
                step="0.01"
                inputMode="decimal"
                value={paramValue}
                onChange={(e) => setParamValue(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setParamDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSaveParam} disabled={updateParamMutation.isPending}>
              {updateParamMutation.isPending ? t('common.saving') : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Avatar Selector */}
      <AvatarSelector
        open={avatarSelectorOpen}
        onOpenChange={setAvatarSelectorOpen}
        currentAvatarId={user?.avatar}
        onSelect={(avatarId) => updateAvatarMutation.mutate(avatarId)}
        isLoading={updateAvatarMutation.isPending}
      />
    </div>
  )
}
